import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { APP_SHAPE } from "../../constants/shape";
import { useConfirm } from "../../hooks/useConfirm";
import type { SupplierAgreementPrice } from "../../types/supplier-agreement.type";
import { FileType, getFileExtension } from "../../types/upload.type";
import { uploadFileAsNewAsset } from "../../services/upload.service";
import {
  getErrorMessage,
  useArchiveSupplierAgreementDocument,
  useAttachSupplierAgreementDocument,
  useSetSupplierAgreementPrice,
  useSupplierAgreementDocuments,
  useSupplierAgreementPrices,
} from "../../pages/suppliers/supplierAgreement.queries";
import SetSupplierAgreementPriceDialog from "./SetSupplierAgreementPriceDialog";

type SnackbarState = { open: boolean; message: string; severity: "success" | "error" };

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
}

export default function SupplierAgreementPanel({
  organizationId,
  supplierId,
  materialIds,
  materialLabels,
  canEdit,
}: {
  organizationId: string;
  supplierId: string;
  materialIds: string[];
  materialLabels: Map<string, string>;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { confirm, confirmDialog } = useConfirm();

  const pricesQuery = useSupplierAgreementPrices(organizationId, supplierId);
  const setPriceMutation = useSetSupplierAgreementPrice(organizationId, supplierId);
  const documentsQuery = useSupplierAgreementDocuments(organizationId, supplierId);
  const attachDocumentMutation = useAttachSupplierAgreementDocument(organizationId, supplierId);
  const archiveDocumentMutation = useArchiveSupplierAgreementDocument(organizationId, supplierId);

  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceDialogMaterialId, setPriceDialogMaterialId] = useState<string | undefined>(undefined);
  const [expandedMaterialId, setExpandedMaterialId] = useState<string | null>(null);
  const [docLabel, setDocLabel] = useState("");
  const [docFile, setDocFile] = useState<File | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: "", severity: "success" });

  const materialOptions = useMemo(
    () => materialIds.map((id) => ({ value: id, label: materialLabels.get(id) ?? id })),
    [materialIds, materialLabels],
  );

  const pricesByMaterial = useMemo(() => {
    const map = new Map<string, SupplierAgreementPrice[]>();
    for (const row of pricesQuery.data ?? []) {
      const list = map.get(row.material_id) ?? [];
      list.push(row);
      map.set(row.material_id, list);
    }
    return map;
  }, [pricesQuery.data]);

  const handleOpenSetPrice = (materialId?: string) => {
    setPriceDialogMaterialId(materialId);
    setPriceDialogOpen(true);
  };

  const handleSetPrice = async (input: { material_id: string; price: string; effective_date: string }) => {
    try {
      await setPriceMutation.mutateAsync(input);
      setPriceDialogOpen(false);
      setSnackbar({ open: true, severity: "success", message: t("suppliers.details.agreement.prices.setPrice") });
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: getErrorMessage(e, t("suppliers.details.agreement.prices.saveError")) });
    }
  };

  const handleAttachDocument = async () => {
    if (!docFile) return;
    if (!getFileExtension(docFile.name)) {
      setSnackbar({ open: true, severity: "error", message: t("suppliers.details.agreement.documents.unsupportedFileType") });
      return;
    }
    try {
      const { asset_id, filename } = await uploadFileAsNewAsset(docFile, FileType.SUPPLIER_AGREEMENT);
      await attachDocumentMutation.mutateAsync({ asset_id, filename, doc_label: docLabel || null });
      setDocFile(null);
      setDocLabel("");
      setSnackbar({ open: true, severity: "success", message: t("suppliers.details.agreement.documents.attach") });
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: getErrorMessage(e, t("suppliers.details.agreement.documents.attachError")) });
    }
  };

  const handleRemoveDocument = async (documentId: string, filename: string) => {
    const confirmed = await confirm({
      title: t("suppliers.details.agreement.documents.remove"),
      description: t("suppliers.details.agreement.documents.removeConfirm", { filename }),
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await archiveDocumentMutation.mutateAsync(documentId);
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: getErrorMessage(e, t("suppliers.details.agreement.documents.loadError")) });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {t("suppliers.details.agreement.subtitle")}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${APP_SHAPE.radiusLg}px`, mb: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t("suppliers.details.agreement.prices.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("suppliers.details.agreement.prices.subtitle")}
            </Typography>
          </Box>
          {canEdit && materialOptions.length > 0 && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenSetPrice(undefined)}>
              {t("suppliers.details.agreement.prices.setPrice")}
            </Button>
          )}
        </Stack>

        {pricesQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : pricesQuery.isError ? (
          <Alert severity="error">{getErrorMessage(pricesQuery.error, t("suppliers.details.agreement.prices.loadError"))}</Alert>
        ) : materialOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("suppliers.details.agreement.prices.emptyState")}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t("suppliers.details.agreement.prices.material")}</TableCell>
                  <TableCell>{t("suppliers.details.agreement.prices.price")}</TableCell>
                  <TableCell>{t("suppliers.details.agreement.prices.effectiveDate")}</TableCell>
                  <TableCell align="right">{t("suppliers.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materialOptions.map((option) => {
                  const history = (pricesByMaterial.get(option.value) ?? []).slice().sort(
                    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                  );
                  const active = history.find((row) => row.status === "active");
                  const superseded = history.filter((row) => row.status !== "active");
                  const expanded = expandedMaterialId === option.value;
                  return (
                    <>
                      <TableRow key={option.value} hover>
                        <TableCell sx={{ width: 40 }}>
                          {superseded.length > 0 && (
                            <IconButton size="small" onClick={() => setExpandedMaterialId(expanded ? null : option.value)}>
                              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{option.label}</TableCell>
                        <TableCell>
                          {active ? (
                            <Chip size="small" color="success" label={`SAR ${active.price}`} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t("suppliers.details.agreement.prices.noActivePrice")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{active ? formatDate(active.effective_date) : "—"}</TableCell>
                        <TableCell align="right">
                          {canEdit && (
                            <Button size="small" onClick={() => handleOpenSetPrice(option.value)}>
                              {t("suppliers.details.agreement.prices.setPrice")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {superseded.length > 0 && (
                        <TableRow>
                          <TableCell sx={{ py: 0, border: 0 }} colSpan={5}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1, pl: 5 }}>
                                <Stack spacing={0.5}>
                                  {superseded.map((row) => (
                                    <Stack key={row.id} direction="row" spacing={1.5} alignItems="center">
                                      <Chip size="small" variant="outlined" label={`SAR ${row.price}`} />
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDate(row.effective_date)}
                                        {row.superseded_at ? ` — ${t("suppliers.details.agreement.prices.status.superseded")} ${formatDate(row.superseded_at)}` : ""}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        <Typography variant="h6" fontWeight={700}>
          {t("suppliers.details.agreement.documents.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("suppliers.details.agreement.documents.subtitle")}
        </Typography>

        {canEdit && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
            <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} sx={{ flex: 1, justifyContent: "flex-start" }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {docFile ? docFile.name : t("suppliers.details.agreement.documents.chooseFile")}
              </Box>
              <input hidden type="file" onChange={(e) => setDocFile(e.target.files?.[0] ?? null)} />
            </Button>
            <TextField
              size="small"
              placeholder={t("suppliers.details.agreement.documents.labelPlaceholder")}
              value={docLabel}
              onChange={(e) => setDocLabel(e.target.value)}
              sx={{ minWidth: { sm: 220 } }}
            />
            <Button
              variant="contained"
              onClick={() => void handleAttachDocument()}
              disabled={!docFile || attachDocumentMutation.isPending}
            >
              {attachDocumentMutation.isPending
                ? t("suppliers.details.agreement.documents.attaching")
                : t("suppliers.details.agreement.documents.attach")}
            </Button>
          </Stack>
        )}

        {documentsQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : documentsQuery.isError ? (
          <Alert severity="error">{getErrorMessage(documentsQuery.error, t("suppliers.details.agreement.documents.loadError"))}</Alert>
        ) : (documentsQuery.data ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("suppliers.details.agreement.documents.emptyState")}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableBody>
                {(documentsQuery.data ?? []).map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell sx={{ width: 32 }}>
                      <DescriptionOutlinedIcon fontSize="small" color="action" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {doc.doc_label || doc.filename}
                      </Typography>
                      {doc.doc_label && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {doc.filename}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(doc.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("suppliers.details.agreement.documents.view")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!doc.file_url}
                            onClick={() => doc.file_url && window.open(doc.file_url, "_blank", "noopener,noreferrer")}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title={t("suppliers.details.agreement.documents.remove")}>
                          <IconButton size="small" onClick={() => void handleRemoveDocument(doc.id, doc.filename)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SetSupplierAgreementPriceDialog
        open={priceDialogOpen}
        submitting={setPriceMutation.isPending}
        materialOptions={materialOptions}
        initialMaterialId={priceDialogMaterialId}
        onClose={() => setPriceDialogOpen(false)}
        onSubmit={(input) => void handleSetPrice(input)}
      />

      {confirmDialog}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
